const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Английские сокращения — запасной вариант, когда язык не передан или
// `Intl` его не знает.
const DAY_SHORT = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

// Код языка витрины → BCP 47. Люксембургский у нас `lu`, а в стандарте `lb`.
const INTL_LOCALE = { lu: 'lb' };

// 2024-01-01 — понедельник: от него отсчитываем остальные дни недели.
const MONDAY_UTC = Date.UTC(2024, 0, 1);

/**
 * Сокращённые названия дней на языке витрины (#1417). Английские «Mon» /
 * «Sun» раньше печатались на любом языке, и автоперевод браузера превращал
 * «Sun» в «Солнце». Названия даёт `Intl`, а не словарь: так они совпадают с
 * тем, как дни пишет сама платформа.
 */
/**
 * `Intl` бросает только на СИНТАКСИЧЕСКИ неверном теге; валидный, но
 * неподдерживаемый (например `lb` на сборке ICU без него) молча берёт язык
 * браузера — и на люксембургской витрине в русском браузере дни вышли бы
 * по-русски. Поэтому сначала спрашиваем `supportedLocalesOf` (ревью !1296).
 */
const buildDayLabels = (language) => {
  const tag = INTL_LOCALE[language] || language;
  try {
    if (Intl.DateTimeFormat.supportedLocalesOf(tag).length === 0) return DAY_SHORT;
    const fmt = new Intl.DateTimeFormat(tag, {
      weekday: 'short',
      timeZone: 'UTC',
    });
    return Object.fromEntries(
      DAY_ORDER.map((day, i) => {
        const label = fmt.format(new Date(MONDAY_UTC + i * 86400000));
        return [day, label.charAt(0).toLocaleUpperCase(language) + label.slice(1)];
      }),
    );
  } catch {
    return DAY_SHORT;
  }
};

const labelCache = new Map();

const dayLabels = (language) => {
  if (!language) return DAY_SHORT;
  if (labelCache.has(language)) return labelCache.get(language);
  const labels = buildDayLabels(language);
  labelCache.set(language, labels);
  return labels;
};

/**
 * Отрезает подпись дня от строки расписания: «Monday: 9:00 - 18:00» → «9:00 - 18:00».
 * Формат приходит из `useContactInfo` (и из URL-параметров дилера), поэтому
 * разбираем терпимо: нет двоеточия — считаем всю строку временем.
 */
const timePart = (line) => {
  if (typeof line !== 'string') return '';
  const idx = line.indexOf(':');
  // «9:00 - 18:00» — первое двоеточие внутри времени, подписи дня нет.
  if (idx === -1 || /^\d/.test(line.trim())) return line.trim();
  return line.slice(idx + 1).trim();
};

/**
 * Сворачивает недельное расписание в диапазоны подряд идущих одинаковых дней.
 *
 * Зачем: дилерский сайт показывает «Mon–Fri 9:00–18:00», а не семь одинаковых
 * строк подряд. До #1330 подвал, «Контакты» и блок шоурумов на главной печатали
 * ровно семь строк «Monday: 9:00 - 18:00 / Tuesday: 9:00 - 18:00 / …» — стена
 * повторяющегося текста, в которой не видно главного: когда открыто.
 *
 * Группировка вычисляется, а не захардкожена: у дилера может быть любое
 * расписание (короткая пятница, закрытая среда), и оно свернётся правильно.
 * Закрытые дни (пустая строка) в вывод не попадают.
 *
 * @param {Record<string, string>} hours — объект вида `{ mon: 'Monday: 9:00 - 18:00', … }`.
 * @param {string} [language] — язык витрины для названий дней; без него — английские.
 * @returns {Array<{ days: string, time: string }>} например `[{days:'Mon–Fri', time:'9:00 - 18:00'}]`.
 */
export function groupOpeningHours(hours, language) {
  if (!hours) return [];
  const labels = dayLabels(language);

  const groups = [];
  for (const day of DAY_ORDER) {
    const time = timePart(hours[day]);
    if (!time) continue; // выходной — строки нет

    const last = groups[groups.length - 1];
    // Продолжаем группу, только если день идёт СРАЗУ за предыдущим: пропуск
    // (закрытая среда) обязан разорвать диапазон, иначе «Mon–Fri» соврёт.
    const isConsecutive =
      last && DAY_ORDER.indexOf(day) === DAY_ORDER.indexOf(last.lastDay) + 1;

    if (last && last.time === time && isConsecutive) {
      last.lastDay = day;
    } else {
      groups.push({ firstDay: day, lastDay: day, time });
    }
  }

  return groups.map(({ firstDay, lastDay, time }) => ({
    days:
      firstDay === lastDay
        ? labels[firstDay]
        : `${labels[firstDay]}–${labels[lastDay]}`,
    time,
  }));
}

export default groupOpeningHours;
